import React, { useContext, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Header } from '@/components/molecules/front-tablet/header'
import { SuccessDialog } from '@/components/organisms/self-checkin/payment/success-dialog'
import { ErrorDialog } from '@/components/organisms/self-checkin/payment/error-dialog'
import * as vescajs from '@/apis/vescajs'
import { LanguageType } from '@/i18n'
import { useQuery } from '@/hooks/use-query'
import { FrontTabletLayout } from '@/components/layouts/front-tablet-layout'
import { useSelfCheckOutState } from '@/hooks/use-self-check-out-state'
import { reportError } from '@/components/apps/error-boundary'
import { debugState } from '@/apis/aipass'
import { ProgressDialog } from '@/components/organisms/self-checkin/payment/progress-dialog'
import { LoaderContextCreator } from '@/contexts/loader'

export const PaymentTerminalWithCard: React.FC = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const query = useQuery()
  const { state, doCheckOut, stateErrorHandling } = useSelfCheckOutState()
  const [receiveTerminalStatus, setReceiveTerminalStatus] = useState<'progress' | 'success' | 'error'>()
  const { setIsLoading } = useContext(LoaderContextCreator())

  // note ブラウザバックなどで決済ページを復元させないために、履歴にスタックさせないようにreplaceをもちいる
  const goBack = () => {
    setIsLoading(false)
    history.replace({ pathname: '/self-checkout/payment' })
  }

  const onSuccess = async (event: vescajs.OutputCompleteEvent) => {
    setIsLoading(false)
    setReceiveTerminalStatus('success')
    try {
      await doCheckOut(
        { ...state, payment: { ...event.pmsResult, kind: 'credit', deviceType: vescajs.DeviceType.PaidCreditCard } },
        i18n.language,
      )
      history.replace({ pathname: '/self-checkout/complete' })
    } catch (e) {
      reportError(e)
      alert(t('Please complete the procedure at the front desk'))
      history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
    }
  }

  useEffect(() => {
    if (!stateErrorHandling(state, 'selectReservation')) {
      return
    }
    const vescaJs = new vescajs.VescaJs({
      hotelId: state.hotelId,
      handler: {
        onCancel: () => {
          setReceiveTerminalStatus(undefined)
          goBack()
        },
        onError: () => {
          setIsLoading(false)
          setReceiveTerminalStatus('error')
          setTimeout(() => goBack(), 5000)
        },
        onProcess: () => {
          setReceiveTerminalStatus('progress')
        },
        onReconnect: () => {
          setIsLoading(true)
        },
        onSuccess,
      },
      paymentTarget: {
        reservationIds: state.selectReservation.selectedReservationIds,
        guestRoomAssignIds: state.selectReservation.selectedReservationAndRooms.flatMap(r => r.guestRoomAssignIds),
      },
    })

    vescaJs.doCreditPay({
      amount: state.selectReservation!.payment!.usage.totalAmount,
      tax: 0,
      lang: i18n.language as LanguageType,
      unionPay: query.has('unionPay'),
    })

    return () => {
      vescaJs.dispose()
    }
  }, [])

  return (
    <FrontTabletLayout state={state}>
      <Header
        goToPreviousPage={() => {
          if (receiveTerminalStatus === 'progress') {
            return
          }
          debugState('card-form', { call: 'co header' })
          goBack()
        }}
        title={t('Payment details')}
      />
      <div css={containerStyle}>
        <div style={{ maxWidth: 880 }}>
          <div className="operation-block">
            <div>{t('Insert or touch the card')}</div>
            <div>{t('To change the payment method, press the red button on the payment terminal.')}</div>
          </div>

          <div className="terminal-and-brand-block">
            <div className="terminal-image-block">
              <img src={require('@/static/images/self-checkin-payment/terminal/image_01.png')} />
              <img src={require('@/static/images/self-checkin-payment/terminal/image_02.png')} />
            </div>
            <div className="brand-image-block">
              <div className="-label">{t('Supported Credits')}</div>
              <div className="-separate"></div>
              <div className="-image">
                <img src={require('@/static/images/self-checkin-payment/terminal/logo_card02.png')} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {receiveTerminalStatus === 'progress' && <ProgressDialog />}
      {receiveTerminalStatus === 'success' && <SuccessDialog />}
      {receiveTerminalStatus === 'error' && <ErrorDialog />}
    </FrontTabletLayout>
  )
}

const containerStyle = css({
  backgroundColor: '#f2f2f2',
  display: 'flex',
  justifyContent: 'center',
  // add header size (=80)
  paddingTop: 32 + 80,
  paddingBottom: 32,
  height: '100vh',
  width: '100%',
  '*': { fontFamily: 'Noto ans JP', color: '#272727' },
  '.operation-block': {
    textAlign: 'center',
    'div:first-of-type': {
      fontSize: 21,
      lineHeight: 1.5,
      fontWeight: 'bold',
      letterSpacing: 3.15,
    },
    'div:last-of-type': {
      paddingTop: 8,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 'bold',
      letterSpacing: 2.4,
      color: '#676767',
    },
  },
  '.terminal-and-brand-block': {
    marginTop: 32,
    backgroundColor: '#ffffff',
    borderRadius: 10,
    border: '1px solid #F2F2F2',
    padding: 40,
    '.terminal-image-block': {
      display: 'flex',
      img: {
        width: '50%',
        ':first-of-type': { marginRight: 12 },
        ':last-of-type': { marginLeft: 12 },
      },
    },
    '.brand-image-block': {
      display: 'flex',
      textAlign: 'center',
      alignItems: 'center',
      border: '1px solid #CCCCCC',
      borderRadius: 10,
      marginTop: 24,
      padding: '20px 40px',
      '.-label': {
        fontSize: 14,
        fontWeight: 'bold',
        letterSpacing: 2.1,
        color: '#676767',
        width: '20%',
        minWidth: '20%',
      },
      '.-separate': {
        margin: '0 40px',
        height: 43,
        borderRight: '1px solid #CCCCCC',
      },
      '.-image': {
        flexGrow: 1,
        img: { width: '100%', objectFit: 'contain', height: 43 },
      },
    },
  },
})
